import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Modal from '@components/common/Modal/Modal'
import { useTaskStore } from '@store'
import taskService from '@services/taskService'
import './TaskActionsModal.css'

/**
 * Task Actions Modal
 *
 * Unified modal for all task actions:
 * - View Task Details
 * - Start Task
 * - Submit Task (with notes)
 * - Accept Task (approver accepts pending review task)
 * - Complete Task (final step - marks workflow as 100% complete)
 * - Approve Task (deprecated - alias for accept)
 * - Reject Task (with remarks - required)
 * - Assign Task (user selection)
 */
const TaskActionsModal = ({
	isOpen,
	onClose,
	task,
	action, // 'view' | 'start' | 'submit' | 'accept' | 'complete' | 'approve' | 'reject' | 'assign'
	userRole,
	availableUsers = [], // For assign action
}) => {
	const navigate = useNavigate()
	const { startTask, submitTask, acceptTask, completeTask, approveTask, rejectTask, assignTaskManually } = useTaskStore()

	const [notes, setNotes] = useState('')
	const [remarks, setRemarks] = useState('')
	const [selectedUserId, setSelectedUserId] = useState('')
	const [evidenceAttached, setEvidenceAttached] = useState(false)
	const [loading, setLoading] = useState(false)

	// For 'view' action, navigate directly to task details page
	React.useEffect(() => {
		if (isOpen && action === 'view' && task?.id) {
			onClose() // Close modal immediately
			navigate(`/tasks/${task.id}`)
		}
	}, [isOpen, action, task?.id, navigate, onClose])

	// Get modal configuration based on action
	const getModalConfig = () => {
		switch (action) {
			case 'view':
				return {
					title: 'Task Details',
					size: 'large',
					showActions: false,
				}
			case 'start':
				return {
					title: 'Start Task',
					size: 'small',
					confirmText: 'Start Task',
					confirmClass: 'btn--primary',
				}
			case 'submit':
				return {
					title: 'Submit Task for Review',
					size: 'medium',
					confirmText: 'Submit',
					confirmClass: 'btn--success',
				}
			case 'accept':
				return {
					title: 'Accept Task',
					size: 'medium',
					confirmText: 'Accept',
					confirmClass: 'btn--success',
				}
			case 'complete':
				return {
					title: 'Complete Task',
					size: 'medium',
					confirmText: 'Complete Task',
					confirmClass: 'btn--success',
				}
			case 'approve':
				return {
					title: 'Approve Task',
					size: 'medium',
					confirmText: 'Approve',
					confirmClass: 'btn--success',
				}
			case 'reject':
				return {
					title: 'Reject Task',
					size: 'medium',
					confirmText: 'Reject',
					confirmClass: 'btn--danger',
				}
			case 'assign':
				return {
					title: 'Assign Task',
					size: 'medium',
					confirmText: 'Assign',
					confirmClass: 'btn--primary',
				}
			default:
				return {
					title: 'Task Action',
					size: 'medium',
				}
		}
	}

	const config = getModalConfig()

	// Handle form submission
	const handleSubmit = async (e) => {
		e.preventDefault()
		setLoading(true)

		try {
			let success = false

			switch (action) {
				case 'start':
					success = await startTask(task.id)
					break

				case 'submit':
					success = await submitTask(task.id, {
						notes,
						evidence_attached: evidenceAttached,
					})
					break

				case 'accept':
					success = await acceptTask(task.id)
					break

				case 'complete':
					success = await completeTask(task.id, {
						notes,
					})
					break

				case 'approve':
					success = await approveTask(task.id, {
						notes,
						evidence_attached: evidenceAttached,
					})
					break

				case 'reject':
					if (!remarks.trim()) {
						alert('Please provide remarks for rejection')
						setLoading(false)
						return
					}
					success = await rejectTask(task.id, remarks)
					break

				case 'assign':
					if (!selectedUserId) {
						alert('Please select a user to assign')
						setLoading(false)
						return
					}
					success = await assignTaskManually(task.id, parseInt(selectedUserId))
					break

				default:
					break
			}

			if (success) {
				handleClose()
			}
		} catch (error) {
			console.error('Task action error:', error)
		} finally {
			setLoading(false)
		}
	}

	// Handle modal close
	const handleClose = () => {
		setNotes('')
		setRemarks('')
		setSelectedUserId('')
		setEvidenceAttached(false)
		onClose()
	}

	// Render modal body based on action
	const renderModalBody = () => {
		if (!task) return null

		switch (action) {
			case 'view':
				return <TaskDetailsView task={task} onClose={handleClose} />

			case 'start':
				return (
					<div className="task-action-content">
						<p className="task-action-message">
							Are you sure you want to start working on this task?
						</p>
						<div className="task-info-box">
							<h4>{task.title}</h4>
							{task.description && <p>{task.description}</p>}
						</div>
					</div>
				)

			case 'submit':
				return (
					<div className="task-action-content">
						<p className="task-action-message">
							Submit this task for review. You can add optional notes for the reviewer.
						</p>

						<div className="form-group">
							<label htmlFor="notes">Notes (Optional)</label>
							<textarea
								id="notes"
								value={notes}
								onChange={(e) => setNotes(e.target.value)}
								placeholder="Add any notes for the reviewer..."
								rows={4}
								className="form-textarea"
							/>
						</div>

						<div className="form-group">
							<label className="checkbox-label">
								<input
									type="checkbox"
									checked={evidenceAttached}
									onChange={(e) => setEvidenceAttached(e.target.checked)}
								/>
								<span>Evidence documents have been attached</span>
							</label>
						</div>
					</div>
				)

			case 'accept':
				return (
					<div className="task-action-content">
						<p className="task-action-message">
							Accept this task to start working on it. The task will be assigned to you.
						</p>

						<div className="task-info-box">
							<h4>{task.title}</h4>
							{task.workflow_info?.current_step && (
								<p className="workflow-step-info">
									Current Step: {task.workflow_info.current_step.role_name || 'N/A'}
								</p>
							)}
						</div>
					</div>
				)

			case 'complete':
				return (
					<div className="task-action-content">
						<div className="alert alert--success">
							<strong>Final Step:</strong> This is the last step in the workflow.
							Completing this task will mark the entire workflow as 100% complete.
						</div>

						<div className="task-info-box">
							<h4>{task.title}</h4>
							{task.workflow_info?.current_step && (
								<p className="workflow-step-info">
									Current Step: {task.workflow_info.current_step.role_name || 'N/A'}
									{' (Final Step)'}
								</p>
							)}
						</div>

						<div className="form-group">
							<label htmlFor="notes">Completion Notes (Optional)</label>
							<textarea
								id="notes"
								value={notes}
								onChange={(e) => setNotes(e.target.value)}
								placeholder="Add any final notes..."
								rows={4}
								className="form-textarea"
							/>
						</div>
					</div>
				)

			case 'approve':
				return (
					<div className="task-action-content">
						<p className="task-action-message">
							Approve this task and advance it to the next workflow step.
						</p>

						<div className="task-info-box">
							<h4>{task.title}</h4>
							{task.workflow_info?.current_step && (
								<p className="workflow-step-info">
									Current Step: {task.workflow_info.current_step.role_name || 'N/A'}
								</p>
							)}
						</div>

						<div className="form-group">
							<label htmlFor="notes">Approval Notes (Optional)</label>
							<textarea
								id="notes"
								value={notes}
								onChange={(e) => setNotes(e.target.value)}
								placeholder="Add approval notes..."
								rows={4}
								className="form-textarea"
							/>
						</div>

						<div className="form-group">
							<label className="checkbox-label">
								<input
									type="checkbox"
									checked={evidenceAttached}
									onChange={(e) => setEvidenceAttached(e.target.checked)}
								/>
								<span>Evidence has been reviewed</span>
							</label>
						</div>
					</div>
				)

			case 'reject':
				return (
					<div className="task-action-content">
						<div className="alert alert--warning">
							<strong>Warning:</strong> Rejecting this task will return it to the previous
							assignee.
						</div>

						<div className="task-info-box">
							<h4>{task.title}</h4>
							{task.workflow_info?.current_step && (
								<p className="workflow-step-info">
									Current Step: {task.workflow_info.current_step.role_name || 'N/A'}
								</p>
							)}
						</div>

						<div className="form-group">
							<label htmlFor="remarks">
								Rejection Remarks <span className="required">*</span>
							</label>
							<textarea
								id="remarks"
								value={remarks}
								onChange={(e) => setRemarks(e.target.value)}
								placeholder="Please explain why you are rejecting this task..."
								rows={4}
								className="form-textarea"
								required
							/>
							<small className="form-help">
								Remarks are required and will be visible to the assignee.
							</small>
						</div>
					</div>
				)

			case 'assign':
				return (
					<div className="task-action-content">
						<p className="task-action-message">
							Assign this task to a team member. The task requires the following role:
						</p>

						<div className="task-info-box">
							<h4>{task.title}</h4>
							{task.workflow_info?.current_step && (
								<p className="workflow-step-info">
									Required Role: {task.workflow_info.current_step.role_name || 'N/A'}
								</p>
							)}
						</div>

						<div className="form-group">
							<label htmlFor="user">
								Select User <span className="required">*</span>
							</label>
							<select
								id="user"
								value={selectedUserId}
								onChange={(e) => setSelectedUserId(e.target.value)}
								className="form-select"
								required
							>
								<option value="">-- Select a user --</option>
								{availableUsers.map((user) => (
									<option key={user.id} value={user.id}>
										{user.first_name} {user.last_name} ({user.role_name})
									</option>
								))}
							</select>
							{availableUsers.length === 0 && (
								<small className="form-help text-danger">
									No users available with the required role.
								</small>
							)}
						</div>
					</div>
				)

			default:
				return null
		}
	}

	// Render modal footer
	const renderModalFooter = () => {
		if (action === 'view') {
			return (
				<button type="button" className="btn btn--outline" onClick={handleClose}>
					Close
				</button>
			)
		}

		return (
			<>
				<button
					type="button"
					className="btn btn--outline"
					onClick={handleClose}
					disabled={loading}
				>
					Cancel
				</button>
				<button
					type="submit"
					className={`btn ${config.confirmClass}`}
					disabled={loading}
					form="task-action-form"
				>
					{loading ? 'Processing...' : config.confirmText}
				</button>
			</>
		)
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			title={config.title}
			size={config.size}
			closeOnBackdrop={false}
			footer={renderModalFooter()}
		>
			<form id="task-action-form" onSubmit={handleSubmit}>
				{renderModalBody()}
			</form>
		</Modal>
	)
}

/**
 * Task Details View Component
 * Displays comprehensive task information
 */
const TaskDetailsView = ({ task, onClose }) => {
	const navigate = useNavigate()
	const statusColor = taskService.getStatusColor(task.workflow_status)
	const statusText = taskService.getStatusText(task.workflow_status)
	const workflowProgress = taskService.getWorkflowProgress(task)
	const isLocked = taskService.isLocked(task)

	// Navigate to unified task details page
	const handleViewDetails = () => {
		if (onClose) onClose()
		navigate(`/tasks/${task.id}`)
	}

	return (
		<div className="task-details">
			{/* Status Badge */}
			<div className="task-details__status">
				<span className={`task-badge task-badge--${statusColor}`}>{statusText}</span>
				{isLocked && (
					<span className="task-badge task-badge--gray">
						<i className="icon-lock"></i> Locked
					</span>
				)}
			</div>

			{/* Basic Information */}
			<div className="task-details__section">
				<h3 className="task-details__section-title">Basic Information</h3>
				<div className="task-details__grid">
					<div className="task-detail-item">
						<span className="task-detail-item__label">Title:</span>
						<span className="task-detail-item__value">{task.title}</span>
					</div>

					{task.description && (
						<div className="task-detail-item task-detail-item--full">
							<span className="task-detail-item__label">Description:</span>
							<span className="task-detail-item__value">{task.description}</span>
						</div>
					)}

					<div className="task-detail-item">
						<span className="task-detail-item__label">Project:</span>
						<span className="task-detail-item__value">{task.project_name || 'N/A'}</span>
					</div>

					<div className="task-detail-item">
						<span className="task-detail-item__label">Assigned To:</span>
						<span className="task-detail-item__value">
							{task.assigned_to_name || 'Unassigned'}
						</span>
					</div>

					{task.due_date && (
						<div className="task-detail-item">
							<span className="task-detail-item__label">Due Date:</span>
							<span className="task-detail-item__value">
								{new Date(task.due_date).toLocaleDateString()}
							</span>
						</div>
					)}

					{task.priority && (
						<div className="task-detail-item">
							<span className="task-detail-item__label">Priority:</span>
							<span className="task-detail-item__value">{task.priority}</span>
						</div>
					)}
				</div>
			</div>

			{/* Workflow Information */}
			{task.workflow_info && (
				<div className="task-details__section">
					<h3 className="task-details__section-title">Workflow Progress</h3>

					{/* Progress Bar */}
					<div className="workflow-progress">
						<div className="workflow-progress__bar">
							<div
								className="workflow-progress__fill"
								style={{ width: `${workflowProgress}%` }}
							/>
						</div>
						<span className="workflow-progress__text">{workflowProgress}% Complete</span>
					</div>

					{/* Current Step */}
					{task.workflow_info.current_step && (
						<div className="task-detail-item">
							<span className="task-detail-item__label">Current Step:</span>
							<span className="task-detail-item__value">
								{task.workflow_info.current_step.role_name || 'N/A'} (Step{' '}
								{task.workflow_info.current_step_index + 1} of{' '}
								{task.workflow_info.total_steps})
							</span>
						</div>
					)}

					{/* Workflow Name */}
					{task.workflow_name && (
						<div className="task-detail-item">
							<span className="task-detail-item__label">Workflow:</span>
							<span className="task-detail-item__value">{task.workflow_name}</span>
						</div>
					)}

					</div>
			)}

			{/* View Full Details Button */}
			<div className="task-details__section">
				<div className="task-details__action">
					<button
						type="button"
						className="btn btn--outline btn--primary btn--sm"
						onClick={handleViewDetails}
					>
						<i className="icon-layers"></i> View Full Details
					</button>
				</div>
			</div>

			{/* Workflow History */}
			{task.workflow_history && Array.isArray(task.workflow_history) && (
				<div className="task-details__section">
					<h3 className="task-details__section-title">Workflow History</h3>
					<div className="workflow-history">
						{task.workflow_history.map((entry, index) => (
							<div key={index} className="workflow-history-item">
								<div className="workflow-history-item__header">
									<span className="workflow-history-item__step">
										{entry.step_name || `Step ${index + 1}`}
									</span>
									<span className="workflow-history-item__date">
										{new Date(entry.timestamp).toLocaleString()}
									</span>
								</div>
								<div className="workflow-history-item__body">
									<p>
										<strong>Action:</strong> {entry.action}
									</p>
									{entry.user_name && (
										<p>
											<strong>By:</strong> {entry.user_name}
										</p>
									)}
									{entry.notes && (
										<p>
											<strong>Notes:</strong> {entry.notes}
										</p>
									)}
								</div>
							</div>
						))}
					</div>
				</div>
			)}

			{/* Timestamps */}
			<div className="task-details__section">
				<h3 className="task-details__section-title">Timestamps</h3>
				<div className="task-details__grid">
					{task.created_at && (
						<div className="task-detail-item">
							<span className="task-detail-item__label">Created:</span>
							<span className="task-detail-item__value">
								{new Date(task.created_at).toLocaleString()}
							</span>
						</div>
					)}

					{task.started_at && (
						<div className="task-detail-item">
							<span className="task-detail-item__label">Started:</span>
							<span className="task-detail-item__value">
								{new Date(task.started_at).toLocaleString()}
							</span>
						</div>
					)}

					{task.completed_at && (
						<div className="task-detail-item">
							<span className="task-detail-item__label">Completed:</span>
							<span className="task-detail-item__value">
								{new Date(task.completed_at).toLocaleString()}
							</span>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

export default TaskActionsModal
